import { Content, isFilled } from '@prismicio/client';
import {
  PrismicLink,
  PrismicRichText,
  SliceComponentProps
} from '@prismicio/react';
import cx from 'classnames';
import PrismicInlineText from 'components/PRISMIC/PrismicInlineText';
import styles from 'components/PRISMIC/scss/pages.module.scss';
import { PageBuilderHeading } from 'components/PRISMIC/slices/Heading/Heading';
import { StaticPropsContext } from 'components/pages/prismic/Page';
import { isEmpty } from 'lodash-es';
import { useContext, type JSX } from 'react';

/**
 * Props for `RssFeed`.
 */
export type RssFeedProps = SliceComponentProps<Content.RssFeedSlice>;

type RssFeedItem = {
  title: string;
  link: string;
  contentSnippet: string;
  'media:thumbnail': {
    $: {
      url: string;
    };
  };
};

/**
 * Component for "RssFeed" Slices.
 */
const RssFeed = ({ slice }: RssFeedProps): JSX.Element | null => {
  const feedItems: RssFeedItem[] =
    useContext(StaticPropsContext)[slice.id]?.items ?? [];

  if (isEmpty(feedItems)) return null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <PageBuilderHeading level="default">
        <PrismicRichText field={slice.primary.heading} />
      </PageBuilderHeading>

      <div className={cx(styles.list, styles.m3)}>
        {feedItems?.map(item => (
          <article
            key={item.title}
            className={cx(styles['list__item'], styles.clearfix)}
          >
            <div className={styles.copy}>
              {slice.primary.display_type.includes('thumbnails') && (
                <a href={item.link}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={item['media:thumbnail']['$'].url}
                    alt={item.title}
                    className={cx(styles.image, styles['image--left'])}
                  />
                </a>
              )}
              <h3 key={item.title} className={cx(styles.h7, styles.m5)}>
                <a href={item.link}>{item.title}</a>
              </h3>
              {slice.primary.display_type.includes('preview') && (
                <div>{item['content:encodedSnippet'].substring(0, 180)}...</div>
              )}
            </div>
          </article>
        ))}
      </div>
      {isFilled.link(slice.primary.link) && (
        <PrismicLink field={slice.primary.link} className={styles.l3}>
          <PrismicInlineText field={slice.primary.link_text} />
        </PrismicLink>
      )}
    </section>
  );
};

export default RssFeed;
